export function MMDDYYYY(date_string: string): string {
	if (date_string == null) return 'invalid date'
	const dateInput = new Date(date_string)
	const date = new Date(dateInput.toISOString().slice(0, -1))

	let year = date.getFullYear()

	let month = (1 + date.getMonth()).toString()
	month = month.length > 1 ? month : '0' + month

	let day = date.getDate().toString()
	day = day.length > 1 ? day : '0' + day

	return month + '/' + day + '/' + year
}

export function prettyDate(date_string: string, tzCentral = false): string {
	if (date_string == null) return 'invalid date'
	let dateInput
	let date

	if (tzCentral) {
		dateInput = new Date(date_string).toLocaleString('en-US', { timeZone: 'America/Chicago' })
		date = new Date(dateInput)
	} else {
		dateInput = new Date(date_string)
		date = new Date(dateInput.toISOString().slice(0, -1))
	}

	let year = date.getFullYear()

	let month = (1 + date.getMonth()).toString()
	month = date.toLocaleString('default', { month: 'short' })

	let day = date.getDate().toString()
	day = day.length > 1 ? day : '0' + day

	return `${month} ${day}, ${year}`
}

export function YYYYMMDD(date_string: Date): string {
	if (date_string == null) return 'invalid date'
	const dateInput = new Date(date_string)
	const date = new Date(dateInput.toISOString().slice(0, -1))

	let year = date.getFullYear()

	let month = (1 + date.getMonth()).toString()
	month = month.length > 1 ? month : '0' + month

	let day = date.getDate().toString()
	day = day.length > 1 ? day : '0' + day

	return year + '-' + month + '-' + day
}

export function sortArrayByDate(array: any[], dateKey: string, ascending = true): any[] {
	return array.sort((a, b) => {
		const dateA = new Date(a[dateKey])
		const dateB = new Date(b[dateKey])
		const utcA = Date.UTC(dateA.getUTCFullYear(), dateA.getUTCMonth(), dateA.getUTCDate())
		const utcB = Date.UTC(dateB.getUTCFullYear(), dateB.getUTCMonth(), dateB.getUTCDate())
		return ascending ? utcA - utcB : utcB - utcA
	})
}

export function getDaysRemaining(aDate: Date, bDate: Date): number {
	const msPerDay = 86400000
	// Use UTC values to avoid DST/timezone shifts
	const utcA = Date.UTC(aDate.getUTCFullYear(), aDate.getUTCMonth(), aDate.getUTCDate())
	const utcB = Date.UTC(bDate.getUTCFullYear(), bDate.getUTCMonth(), bDate.getUTCDate())
	return Math.floor((utcA - utcB) / msPerDay)
}

export function getTimeDiff(x: Date, y: Date) {
	return Math.floor(Date.parse(x.toISOString()) - Date.parse(y.toISOString()))
}

/**
 * Outputs the date and parsed date comparison functions
 *
 * const sepStart = new ShopDate(new Date('12/16/2025'))
 */
export class DateItem {
	date: Date
	divider: '-' | '/'

	constructor(args: {
		date?: string
		hasTime?: boolean
		divider?: 'hyphen' | 'slash'
		inputFormat?: 'MM/DD/YYYY'
	}) {
		const hasTime = args.hasTime ?? false

		let dateStr = args.date
		if (dateStr && args.inputFormat === 'MM/DD/YYYY') {
			const [month, day, year] = dateStr.split('/')
			dateStr = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
		}

		const date: Date = dateStr
			? hasTime
				? new Date(dateStr)
				: new Date(`${dateStr}T00:00:00`)
			: new Date()

		this.date = date
		this.divider = args.divider === 'hyphen' ? '-' : '/'
	}

	get parsed(): number {
		return Date.parse(this.date.toLocaleString())
	}

	get yyyymmdd(): string {
		return YYYYMMDD(this.date)
	}

	get mmddyyyy(): string {
		const mm = String(this.date.getMonth() + 1).padStart(2, '0')
		const dd = String(this.date.getDate()).padStart(2, '0')
		const yyyy = this.date.getFullYear()
		return `${mm}${this.divider}${dd}${this.divider}${yyyy}`
	}

	get normalized(): Date {
		return new Date(`${this.yyyymmdd}`)
	}
}

interface DateValidationResult {
	isValid: boolean
	error?: string
	parsedDate?: Date
}

/**
 * Validates a date string in MM/DD/YYYY format and returns detailed validation results.
 *
 * This function performs strict validation by:
 * 1. Checking the format matches MM/DD/YYYY
 * 2. Parsing the date components
 * 3. Verifying the date is valid (handles leap years, month boundaries, etc.)
 * 4. Ensuring JavaScript's Date object didn't roll over to a different date
 *
 * @param dateString - The date string to validate in MM/DD/YYYY format
 * @returns A DateValidationResult object containing:
 *   - isValid: true if the date is valid, false otherwise
 *   - error: Error message if validation failed (undefined if valid)
 *   - parsedDate: JavaScript Date object if valid (undefined if invalid)
 *
 * @example
 * ```typescript
 * // Valid leap year date
 * const result1 = validateDate('02/29/2000');
 * // Returns: { isValid: true, parsedDate: Date object }
 *
 * // Invalid leap year date
 * const result2 = validateDate('02/29/2001');
 * // Returns: { isValid: false, error: 'Invalid date (e.g., Feb 29 in non-leap year)' }
 *
 * // Invalid format
 * const result3 = validateDate('2001-02-29');
 * // Returns: { isValid: false, error: 'Invalid format. Expected MM/DD/YYYY' }
 *
 * // Invalid day
 * const result4 = validateDate('02/30/2020');
 * // Returns: { isValid: false, error: 'Invalid date (e.g., Feb 29 in non-leap year)' }
 * ```
 *
 * @remarks
 * This function correctly handles:
 * - Leap years (including century rules: 2000 is a leap year, 1900 is not)
 * - Month boundaries (e.g., 30 vs 31 days)
 * - Invalid months (> 12)
 * - Invalid days (> 31 or month-specific limits)
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date | MDN Date Documentation}
 */
export function validateDate(dateString: string): DateValidationResult {
	// Parse MM/DD/YYYY format
	const match = dateString.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)

	if (!match) {
		return { isValid: false, error: 'Invalid format. Expected MM/DD/YYYY' }
	}

	// Extract and validate capture groups exist
	const month = match[1]
	const day = match[2]
	const year = match[3]

	if (!month || !day || !year) {
		return { isValid: false, error: 'Invalid format. Expected MM/DD/YYYY' }
	}

	const monthNum = parseInt(month, 10)
	const dayNum = parseInt(day, 10)
	const yearNum = parseInt(year, 10)

	const date = new Date(yearNum, monthNum - 1, dayNum)

	// Check if the date components match what we parsed
	// If JavaScript rolled over the date (e.g., Feb 30 -> Mar 2), components won't match
	const isValid =
		date.getFullYear() === yearNum && date.getMonth() === monthNum - 1 && date.getDate() === dayNum

	if (!isValid) {
		return { isValid: false, error: 'Invalid date (e.g., Feb 29 in non-leap year)' }
	}

	return { isValid: true, parsedDate: date }
}

// Inclusive range check for date strings; returns false if any input is invalid
export function isBetweenDates(value: string, start: string, end: string): boolean {
	const v = Date.parse(value)
	const s = Date.parse(start)
	const e = Date.parse(end)

	// Date.parse returns NaN on bad input; reject explicitly
	if ([v, s, e].some(Number.isNaN)) return false

	// Tolerate swapped bounds by normalizing the order
	const lo = Math.min(s, e)
	const hi = Math.max(s, e)

	return v >= lo && v <= hi
}
